import React, { useState, useEffect } from 'react';
import type { PropertyAnalysisResult } from '../../types/calculations';
import { ShieldCheck, AlertTriangle, Hammer } from 'lucide-react';
import { PropertyIntelligencePanel } from './PropertyIntelligencePanel';
import { NegotiationStrategyCard } from './NegotiationStrategyCard';
import { CompsSection } from './CompsSection';
import { PermitHeatmap } from './PermitHeatmap';
import { SectionTitle, GoldBadge, LuxuryTable } from '../Luxury';
import { useActiveMarket } from '../../store/useMarketStore';

/** Permit analysis response from /api/permits/analysis endpoint */
interface PermitAnalysisData {
    property_permit_count: number;
    activity_classification: 'cold_spot' | 'below_avg' | 'average' | 'hot_spot';
    renovation_ratio?: number | null;
    permit_lag_months?: number | null;
    permit_lag_correlation?: number | null;
    permit_density_per_1000?: number | null;
    classification_breakdown?: Record<string, number> | null;
}

interface Tab3Props {
    data: PropertyAnalysisResult;
    propertyId: string;
    onRefresh?: () => void;
    isRefreshing?: boolean;
}

export const Tab3Agents: React.FC<Tab3Props> = ({ data, propertyId, onRefresh, isRefreshing }) => {
    const market = useActiveMarket();
    const license = data.rental_license || { status: 'NOT_LOADED' };
    const permits = data.permit_history || [];

    // Permit analysis state for heatmap (56-16)
    const [permitAnalysis, setPermitAnalysis] = useState<PermitAnalysisData | null>(null);
    const [permitLoading, setPermitLoading] = useState(false);
    const [permitError, setPermitError] = useState<string | null>(null);

    // Fetch permit analysis data
    useEffect(() => {
        if (!propertyId) return;

        const fetchPermitAnalysis = async () => {
            setPermitLoading(true);
            setPermitError(null);
            try {
                const response = await fetch(`/api/permits/analysis/${propertyId}`);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const analysisData = await response.json();
                setPermitAnalysis(analysisData);
            } catch (err) {
                console.error('Failed to fetch permit analysis:', err);
                setPermitError('Unable to load permit analysis');
            } finally {
                setPermitLoading(false);
            }
        };

        fetchPermitAnalysis();
    }, [propertyId]);

    const hasRecentMEP = permits.some((p: any) =>
        ['ELECTRICAL', 'PLUMBING', 'MECHANICAL'].some(t => p.type?.toUpperCase().includes(t))
    );

    // Get last permit date for intelligence panel
    const lastPermitDate = permits.length > 0
        ? permits.reduce((latest: string, p: any) => {
              const pDate = p.issue_date || '';
              return pDate > latest ? pDate : latest;
          }, '')
        : undefined;

    // Get rent validation confidence and methodology
    const rentConfidence = data.rent_validation?.current_rent?.confidence_level
        || data.rent_capex_transparency?.current_rent_confidence;
    const rentMethodology = data.rent_validation?.methodology;

    return (
        <div className="space-y-6 bg-obsidian min-h-full">
            {/* NEGOTIATION STRATEGY (Phase 76) */}
            <NegotiationStrategyCard data={data} />

            {/* SECTION A: PROPERTY INTELLIGENCE PANEL (4-card grid) */}
            <PropertyIntelligencePanel
                propertyId={propertyId}
                rentalLicense={{
                    status: license.status || 'UNKNOWN',
                    unit_count: license.unit_count,
                    expiration_date: license.expiration_date,
                    license_number: license.license_number,
                }}
                permitCount={permits.length}
                lastPermitDate={lastPermitDate}
                unitConfidence={data.unit_confidence}
                unitSource={data.unit_source}
                rentConfidence={rentConfidence}
                rentMethodology={rentMethodology}
                dataFetchedAt={data.calculated_at}
                onRefresh={onRefresh}
                isRefreshing={isRefreshing}
            />

            {/* SECTION B: MEP SYSTEM RISK */}
            <div className={`bg-charcoal p-6 rounded-lg border-l-4 ${hasRecentMEP ? 'border-emerald' : 'border-amber-500'}`}>
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="text-lg font-serif italic text-gold">MEP System Risk</h3>
                        <div className="mt-4 flex items-center gap-3">
                            {hasRecentMEP ? (
                                <ShieldCheck className="w-7 h-7 text-emerald" />
                            ) : (
                                <AlertTriangle className="w-7 h-7 text-amber-400" />
                            )}
                            <span className="text-2xl font-bold font-mono text-ivory">
                                {hasRecentMEP ? "Low Risk" : "Value-Add"}
                            </span>
                        </div>
                    </div>
                    <GoldBadge variant={hasRecentMEP ? "ssot" : "gold"}>
                        {hasRecentMEP ? "Modern" : "Original?"}
                    </GoldBadge>
                </div>

                <div className="mt-6 text-sm border-t border-slate-dark pt-4">
                    {hasRecentMEP ? (
                        <p className="text-emerald">
                            Recent permits found for Mechanical, Electrical, or Plumbing work.
                            Systems likely modernized.
                        </p>
                    ) : (
                        <p className="text-amber-400">
                            No recent MEP permits found in last 10 years.
                            Budget likely needs to account for system upgrades (included in CapEx).
                        </p>
                    )}
                </div>
            </div>

            {/* SECTION C: PERMIT ACTIVITY HEATMAP (56-16) */}
            {data.inputs?.latitude && data.inputs?.longitude ? (
                <PermitHeatmap
                    latitude={data.inputs.latitude}
                    longitude={data.inputs.longitude}
                    nearbyPermitCount={permitAnalysis?.property_permit_count || permits.length}
                    densityClassification={permitAnalysis?.activity_classification || 'average'}
                    isLoading={permitLoading}
                    error={permitError || undefined}
                    permitIntelligence={permitAnalysis ? {
                        renovation_ratio: permitAnalysis.renovation_ratio,
                        permit_lag_months: permitAnalysis.permit_lag_months,
                        permit_lag_correlation: permitAnalysis.permit_lag_correlation,
                        permit_density_per_1000: permitAnalysis.permit_density_per_1000,
                        classification_breakdown: permitAnalysis.classification_breakdown,
                    } : null}
                />
            ) : (
                <div className="bg-charcoal p-6 rounded-lg border border-slate-dark">
                    <SectionTitle icon={<Hammer className="w-5 h-5" />}>Permit Activity Heatmap</SectionTitle>
                    <p className="text-stone-700 text-sm mt-4">
                        Location coordinates not available for this property. Heatmap requires latitude and longitude data.
                    </p>
                </div>
            )}

            {/* SECTION D: MARKET COMPARABLES */}
            <div className="bg-charcoal p-6 rounded-lg border border-slate-dark">
                <SectionTitle icon={<Hammer className="w-5 h-5" />}>Market Comparables</SectionTitle>
                <CompsSection propertyId={propertyId} />
            </div>

            {/* SECTION E: PERMIT HISTORY TABLE */}
            <div className="bg-charcoal p-6 rounded-lg border border-slate-dark">
                <SectionTitle icon={<Hammer className="w-5 h-5" />}>Recent Permit History</SectionTitle>
                <LuxuryTable>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Description</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {permits.length > 0 ? (
                            permits.map((permit: any, idx: number) => (
                                <tr key={idx}>
                                    <td className="num">{permit.issue_date ? new Date(permit.issue_date).toLocaleDateString() : 'N/A'}</td>
                                    <td>
                                        <span className="inline-block px-2 py-0.5 text-sm font-semibold uppercase tracking-wider bg-graphite border border-slate-dark rounded text-stone-700">
                                            {permit.type || 'UNKNOWN'}
                                        </span>
                                    </td>
                                    <td className="max-w-md truncate" title={permit.description || ''}>
                                        {permit.description || 'No description'}
                                    </td>
                                    <td>
                                        <span className={`text-sm font-semibold ${permit.status === 'COMPLETED' || permit.status === 'ISSUED' ? 'text-emerald' : 'text-stone-700'}`}>
                                            {permit.status || 'UNKNOWN'}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={4} className="text-center text-stone-700 py-8">
                                    {data.permit_history === undefined
                                        ? 'Loading permit history...'
                                        : `No permits found in ${market.permit_source_name} database for this property (last 10 years).`}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </LuxuryTable>
            </div>
        </div>
    );
};
